import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/app/page-header'
import { MetricGrid } from '@/components/metric-tile'
import { StatusBadge as HealthBadge } from '@/components/status-badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { isVisualRegression } from '@/lib/incident'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiV2 } from '@/lib/api-v2-client'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/use-auth'
import { useUpgradePrompt } from '@/components/upgrade-prompt'
import { ProFeatureNotice } from '@/components/pro-feature-notice'
import { hasPaidPlan, isFreeTier } from '@/lib/subscription-access'
import { formatSeconds, formatSince } from '@/lib/format-duration'

export const Route = createFileRoute('/_authenticated/dashboard/incidents/$id')({
  component: IncidentDetailPage,
})

function IncidentDetailPage() {
  const { id } = Route.useParams()
  const queryClient = useQueryClient()
  // Posting an incident update is a Pro route: the server answers 402 for the free plan.
  const { organization } = useAuth()
  const freeTier = isFreeTier(organization)

  const { data: incidentResponse, isLoading } = useQuery({
    queryKey: ['incident', id],
    queryFn: () => apiV2.getIncident(id),
  })

  // v2 detail is nested: { data: { incident, updates } } (legacy was flat).
  const incident = incidentResponse?.data?.incident
  const updates = incidentResponse?.data?.updates ?? []

  const resolveMutation = useMutation({
    mutationFn: () => apiV2.resolveIncident(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident', id] })
      queryClient.invalidateQueries({ queryKey: ['incidents'] })
      toast.success('Incident resolved')
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to resolve')
    },
  })

  const acknowledgeMutation = useMutation({
    mutationFn: () => apiV2.acknowledgeIncident(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident', id] })
      queryClient.invalidateQueries({ queryKey: ['incidents'] })
      toast.success('Incident acknowledged — escalation paused')
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to acknowledge')
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded-lg bg-muted" />
      </div>
    )
  }

  if (!incident) {
    return (
      <div className="py-12 text-center">
        <h1 className="text-xl font-semibold">Incident not found</h1>
        <Button asChild className="mt-4">
          <Link to="/dashboard/incidents">Back to Incidents</Link>
        </Button>
      </div>
    )
  }

  const isOngoing = incident.status === 'ongoing'

  return (
    <div className="space-y-6">
      <PageHeader
        back={{ label: 'Incidents', to: '/dashboard/incidents' }}
        title={incident.monitor_name || 'Incident'}
        badges={
          <>
            <HealthBadge kind={isOngoing ? 'down' : 'up'} label={isOngoing ? 'Ongoing' : 'Resolved'} />
            {isVisualRegression(incident.cause) && <HealthBadge kind="degraded" label="Visual change" />}
            {incident.acknowledged_at && (
              <Badge variant="outline" className="border-border text-primary">
                Acknowledged
              </Badge>
            )}
          </>
        }
        description={
          (incident.cause || isOngoing) && (
            <>
              {incident.cause && <p className="text-body">{incident.cause}</p>}
              {isOngoing && <OnCallNow monitorId={incident.monitor_id} />}
            </>
          )
        }
        primaryAction={
          isOngoing && !incident.acknowledged_at ? (
            <Button onClick={() => acknowledgeMutation.mutate()} disabled={acknowledgeMutation.isPending}>
              {acknowledgeMutation.isPending ? 'Acknowledging...' : 'Acknowledge'}
            </Button>
          ) : isOngoing ? (
            <Button onClick={() => resolveMutation.mutate()} disabled={resolveMutation.isPending}>
              {resolveMutation.isPending ? 'Resolving...' : 'Resolve incident'}
            </Button>
          ) : undefined
        }
        menu={
          isOngoing && !incident.acknowledged_at
            ? [{ label: 'Resolve incident', disabled: resolveMutation.isPending, onSelect: () => resolveMutation.mutate() }]
            : undefined
        }
      />

      <MetricGrid
        aria-label="Incident timing"
        className="grid-cols-1 sm:grid-cols-3 lg:grid-cols-3"
        items={[
          { label: 'Started', value: incident.started_at ? new Date(incident.started_at).toLocaleString() : 'Unknown', mono: !!incident.started_at },
          {
            label: 'Resolved',
            value: incident.resolved_at ? new Date(incident.resolved_at).toLocaleString() : 'Ongoing',
            mono: !!incident.resolved_at,
            tone: incident.resolved_at ? undefined : 'down',
          },
          {
            label: 'Duration',
            value:
              incident.duration != null
                ? formatSeconds(incident.duration)
                : incident.started_at
                  ? formatSince(incident.started_at)
                  : '—',
          },
        ]}
      />

      <IncidentAiSummary incidentId={id} />

      {isOngoing &&
        (freeTier ? (
          <ProFeatureNotice title="Incident updates are on Pro">
            Post status updates on an incident. This needs a paid plan.
          </ProFeatureNotice>
        ) : (
          <PostUpdateForm incidentId={id} />
        ))}

      <Card>
        <CardHeader>
          <CardTitle>Timeline</CardTitle>
          <CardDescription>Status updates for this incident</CardDescription>
        </CardHeader>
        <CardContent>
          {updates.length > 0 ? (
            <div className="space-y-4">
              {updates.map((update) => (
                <div key={update.id} className="flex gap-4 border-l-2 pl-4 pb-4 border-muted">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={update.status} />
                      <span className="font-medium">{update.title}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{update.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {update.posted_at ? new Date(update.posted_at).toLocaleString() : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No updates posted yet
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function PostUpdateForm({ incidentId }: { incidentId: string }) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState('investigating')

  const mutation = useMutation({
    mutationFn: () => apiV2.createIncidentUpdate(incidentId, { title, description, status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident', incidentId] })
      queryClient.invalidateQueries({ queryKey: ['incidents'] })
      toast.success('Update posted')
      setTitle('')
      setDescription('')
      setStatus('investigating')
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to post update')
    },
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Post Update</CardTitle>
        <CardDescription>Add a status update to this incident</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            mutation.mutate()
          }}
          className="space-y-4"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="update-title">Title</Label>
              <Input
                id="update-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Investigating the issue"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="update-status">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="update-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="identified">Identified</SelectItem>
                  <SelectItem value="monitoring">Monitoring</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="update-desc">Description</Label>
            <Input
              id="update-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Details about what's happening..."
              required
            />
          </div>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Posting...' : 'Post Update'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    investigating: 'bg-status-degraded-soft text-status-degraded',
    identified: 'bg-status-paused-soft text-status-paused',
    monitoring: 'bg-status-unknown-soft text-status-unknown',
    resolved: 'bg-status-up-soft text-status-up',
  }

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${variants[status] || ''}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

function IncidentAiSummary({ incidentId }: { incidentId: string }) {
  const { organization } = useAuth()
  const { show: showUpgradePrompt } = useUpgradePrompt()
  const [summary, setSummary] = useState<string | null>(null)
  const [classification, setClassification] = useState<string | null>(null)
  const mutation = useMutation({
    mutationFn: () => apiV2.explainIncident(incidentId),
    onSuccess: (r) => {
      setSummary(r.summary)
      setClassification(r.classification)
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not generate a summary'),
  })
  const isPaid = hasPaidPlan(organization)
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base">AI root-cause summary</CardTitle>
            <CardDescription>Plain-English explanation from the captured failures.</CardDescription>
          </div>
          {!summary && (
            <Button
              size="sm"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => {
                if (!isPaid) {
                  showUpgradePrompt(
                    'AI root-cause summaries explain what broke — in plain English, from the actual failure data. Available on paid plans.',
                  )
                  return
                }
                mutation.mutate()
              }}
            >
              {mutation.isPending ? 'Analyzing…' : '✨ Explain this incident'}
            </Button>
          )}
        </div>
      </CardHeader>
      {summary && (
        <CardContent>
          {(() => {
            const badge: Record<string, { label: string; cls: string }> = {
              true_failure: { label: 'True failure', cls: 'bg-status-down-soft text-status-down' },
              misconfiguration: { label: 'Misconfiguration', cls: 'bg-status-degraded-soft text-status-degraded' },
              insufficient_data: { label: 'Insufficient data', cls: 'bg-status-unknown-soft text-status-unknown' },
            }
            const b = classification ? badge[classification] : null
            return b ? (
              <span className={`mb-2 inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${b.cls}`}>
                {b.label}
              </span>
            ) : null
          })()}
          <p className="whitespace-pre-line text-sm text-body">{summary}</p>
          <p className="mt-2 text-xs text-muted-foreground">
            Generated by AI from this incident&rsquo;s data — verify before acting.
          </p>
        </CardContent>
      )}
    </Card>
  )
}

/**
 * "On call now" chips for an ongoing incident (92-add-on-call-scheduling 5.3):
 * monitor → escalation policy → steps that target an on-call schedule → who is
 * on call for each right now. Renders nothing when the monitor has no policy,
 * the policy pages no schedule, or a lookup fails (e.g. a free plan).
 */
function OnCallNow({ monitorId }: { monitorId: string }) {
  const { data: monitor } = useQuery({
    queryKey: ['monitor', monitorId],
    queryFn: () => apiV2.getMonitor(monitorId),
    retry: false,
  })
  const policyId = monitor?.data?.escalation_policy_id ?? null
  const { data: policies } = useQuery({
    queryKey: ['escalation-policies'],
    queryFn: () => apiV2.listEscalationPolicies(),
    enabled: !!policyId,
    retry: false,
  })
  const { data: schedules } = useQuery({
    queryKey: ['on-call-schedules'],
    queryFn: () => apiV2.listOnCallSchedules(),
    enabled: !!policyId,
    retry: false,
  })
  const policy = policies?.data.find((p) => p.id === policyId)
  const scheduleIds = [
    ...new Set(
      (policy?.steps ?? []).map((s) => s.on_call_schedule_id).filter((sid): sid is string => !!sid),
    ),
  ]
  if (scheduleIds.length === 0) return null
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
      {scheduleIds.map((sid) => (
        <OnCallChip key={sid} scheduleId={sid} name={schedules?.data.find((s) => s.id === sid)?.name} />
      ))}
    </div>
  )
}

function OnCallChip({ scheduleId, name }: { scheduleId: string; name?: string }) {
  const { data } = useQuery({
    queryKey: ['on-call-current', scheduleId],
    queryFn: () => apiV2.getOnCallCurrent(scheduleId),
    refetchInterval: 60_000,
    retry: false,
  })
  if (!data?.participant) return null
  return (
    <Badge variant="outline" title={data.at ? `Shift ends ${new Date(data.at).toLocaleString()}` : undefined}>
      On call{name ? ` (${name})` : ''}: {data.participant.id}
    </Badge>
  )
}
